import { Avatar, Box, Card, CardActionArea, CardHeader, Grid, Typography } from '@mui/material'
import { LoginForm } from '@sendhub/integrations-ui'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { selectRefreshToken, login, checkAuth, selectSsoProfile, selectSsoSettings, checkSso, doSso } from '../../../store/auth/auth.slice'
import { useAppDispatch } from '../../../store/root'
import Image from 'mui-image'
import { UserProfile, UserSettings } from '@sendhub/api-client-typescript-axios'

// TODO: move this to shared utils
export function isLocation(obj: unknown): obj is Location {
  return typeof obj === 'object' && obj !== null && 'pathname' in obj && 'search' in obj && 'hash' in obj && 'key' in obj && 'state' in obj
}

export function LoginPage() {
  const isLoggedIn = !!useSelector(selectRefreshToken)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const routerQueryParams = Object.fromEntries(new URLSearchParams(location.state?.queryParams || '').entries())
  const urlQueryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())
  const queryParams = { ...routerQueryParams, ...urlQueryParams }
  const _from = location.state?.from
  const from = isLocation(_from) ? _from : undefined

  const profile = useSelector(selectSsoProfile)
  const settings = useSelector(selectSsoSettings)

  // Check if user is logged in to integrations and also app.sendhub.com
  useEffect(() => {
    if (isLoggedIn) return
    const currentHost = new URL(window.location.href).host

    if (
      (!currentHost.includes('int') && !currentHost.includes('localhost')) || // If we're not in the integration site direct, assume we're embedded and doSso
      ('iframe' in queryParams && queryParams['iframe'] && queryParams['iframe'] !== '0') ||
      ('sso' in queryParams && queryParams['sso'] && queryParams['sso'] !== '0')
    ) {
      dispatch(doSso())
    } else {
      dispatch(checkAuth())
      dispatch(checkSso())
    }
  }, [isLoggedIn, location])

  useEffect(() => {
    if (isLoggedIn) {
      const searchParams = new URLSearchParams(queryParams)
      searchParams.delete('sso')
      const queryString = searchParams.toString()

      const hashParams = new URLSearchParams(location.state?.hash || {})
      const hashString = hashParams.toString().replace(/^%23/, '#')

      const path = (from?.pathname || '/integrations') + (queryString ? `?${queryString}` : '') + (hashString ? `?${hashString}` : '')
      navigate(path)
    }
  }, [isLoggedIn, location, from])

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center">
      <LoginForm onLogin={(creds) => dispatch(login(creds))} />
      {profile && settings && (
        <Box sx={{ alignItems: 'flex-start', marginTop: 2 }}>
          <Typography variant="h6" sx={{ marginLeft: 1 }}>
            SendHub Sessions
          </Typography>
          <SsoCard profile={profile} settings={settings} />
        </Box>
      )}
    </Grid>
  )
}

export interface SsoCardProps {
  profile: UserProfile
  settings: UserSettings
}
export function SsoCard({ profile, settings }: SsoCardProps) {
  const dispatch = useAppDispatch()
  const ssoIsAdmin = profile?.auth_groups && profile.auth_groups.some((ag) => (ag as any).name === 'Account Administrators')
  return (
    <Card
      sx={{ width: 360 }}
      onClick={() => {
        dispatch(doSso())
      }}
    >
      <CardActionArea>
        <CardHeader
          sx={{
            '& .MuiCardHeader-content': {
              maxWidth: '86%',
            },
          }}
          avatar={
            settings.image ? (
              <Avatar>
                <Image
                  src={settings.image}
                  duration={600}
                  easing="cubic-bezier(0.7, 0, 0.6, 1)"
                  shift="top"
                  distance="20px"
                  shiftDuration={180}
                  bgColor="inherit"
                />
              </Avatar>
            ) : (
              <Avatar />
            )
          }
          title={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                maxWidth: '100%',
              }}
            >
              <Typography noWrap variant="inherit">
                {profile.name}
              </Typography>
              <Typography noWrap variant="inherit" sx={{ marginLeft: 2 }}>
                {profile.enterprise}
              </Typography>
            </Box>
          }
          subheader={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                maxWidth: '100%',
              }}
            >
              <Typography noWrap variant="inherit">
                {profile.sendhub_number}
              </Typography>
              {ssoIsAdmin && (
                <Typography noWrap variant="inherit" sx={{ marginLeft: 2 }}>
                  Admin
                </Typography>
              )}
            </Box>
          }
        />
      </CardActionArea>
    </Card>
  )
}
